import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, HelpCircle, ArrowRight, ShieldCheck, Users } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";

/**
 * Public pricing page. Used by consumers to compare pricing AND by LLM
 * search engines (Copilot, ChatGPT, Gemini, Perplexity) which look for
 * clear pricing, credibility, and "who is this for" signals when ranking
 * results.
 *
 * Route: /pricing (public — no auth required)
 *
 * Pricing numbers are fetched live from /api/pricing/public so that when
 * an admin edits fees on /admin/pricing, this page reflects them
 * immediately (no redeploy needed).
 */

// Fallback numbers — used only if the /pricing/public call fails (network
// error, cold start, etc.). Must stay in sync with backend defaults.
const FALLBACK = { base: 345, add: 245 };

const money = (dollars) =>
  `$${Number(dollars).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function buildTiers(base, add) {
  return [
  {
    name: "Free",
    price: "$0",
    period: "to start",
    description:
      "Start your case, upload documents, and see how much of the paperwork the platform can prepare for you — with no commitment.",
    cta: "Start free",
    ctaLink: "/register",
    highlight: false,
    features: [
      "Create your family-based case",
      "Upload documents in any language",
      "See your case preparation progress",
      "Multilingual interface & guidance",
      "Save your progress and return anytime",
    ],
  },
  {
    name: "Prepare & File",
    price: money(base),
    period: "per DS-260 applicant",
    description:
      `Everything you need to take your family from I-130 approval to the consulate interview. Each DS-260 applicant is ${money(base)}. Additional family members on the same NVC case are ${money(add)} each — every person gets their own complete DS-260.`,
    cta: "Get started",
    ctaLink: "/register",
    highlight: true,
    features: [
      "Everything in Free",
      "Every family member's DS-260 drafted from your documents",
      "DS-260 Consular Processing INCLUDED (per person)",
      `${money(add)} per dependent family member (includes their DS-260)`,
      "Civil-documents checklist (NVC cover sheet order)",
      "Printable DS-260 worksheets for your records",
      "NVC fee and stage tracking through the consulate interview",
      "Case activity email updates",
      "Priority support",
    ],
  },
  {
    name: "Prepare + Attorney Review",
    price: "Coming soon",
    period: "prepay + attorney fee",
    description:
      "Optional professional review by a licensed U.S. immigration attorney before you file — for extra peace of mind on complex cases.",
    cta: "Notify me",
    ctaLink: "/register",
    highlight: false,
    features: [
      "Everything in Prepare & File",
      "Read-only review by a licensed attorney",
      "Flagged items with attorney notes",
      "Final approval sign-off",
      "Attorney selects from vetted directory or bring your own",
    ],
  },
  ];
}

function buildFaq(base, add) {
  return [
  {
    q: "How does pricing work when I'm filing for multiple family members?",
    a: `The primary applicant pays ${money(base)} for their DS-260 case. Each additional family member on the same NVC case (spouse and children) is ${money(add)} — and every family member gets their own complete DS-260. Government fees (like the $345 IV fee per applicant) are separate and paid only at ceac.state.gov.`,
  },
  {
    q: "Is DS-260USA.com a law firm?",
    a: "No. DS-260USA.com is a self-serve preparation platform designed by a licensed U.S. immigration attorney (in practice since 1980, 35+ years handling immigration cases exclusively). We help you prepare and organize your paperwork — you review every form before submission. For legal advice specific to your situation, you can invite a licensed attorney to review your case through our optional professional review.",
  },
  {
    q: "What forms does DS-260USA.com prepare?",
    a: "A complete consular package for every family member: their DS-260 (Immigrant Visa Electronic Application) drafted from your documents and ready to enter at ceac.state.gov, a civil-documents checklist matched to NVC's own cover sheet, and fee/stage tracking from the NVC fee bill to the interview.",
  },
  {
    q: "Which family relationships are supported?",
    a: "Any family with an approved I-130 processing through a U.S. consulate abroad: spouses, children, parents, and siblings — including the whole family group (primary applicant plus derivative spouse and children) on one NVC case.",
  },
  {
    q: "Do you support consular processing (DS-260)?",
    a: "Yes — and it's INCLUDED in your pricing. Every intending immigrant (primary beneficiary and each dependent) receives their own DS-260 preparation covering all major sections: passport, nationality, U.S. entry intent, and required security disclosures. You never pay extra for DS-260.",
  },
  {
    q: "What if my documents are not in English?",
    a: "Upload documents in their original language — Spanish, Chinese, Hindi, Tagalog, Vietnamese, Arabic, and many more. Our platform reads your documents and helps you organize the required information into every family member's DS-260.",
  },
  {
    q: "When do I pay?",
    a: "Never at signup. You only pay when you're ready to generate your final filing packet. If you decide the platform isn't the right fit before then, you owe nothing.",
  },
  {
    q: "Can I invite my own attorney to review my case?",
    a: "Yes. You can invite any licensed immigration attorney to review your case in a read-only workspace before you file — at no additional cost from us. Just enter their email address from your case dashboard.",
  },
  {
    q: "How secure is my data?",
    a: "Documents are encrypted at rest and in transit. Only you and any attorney you invite can see your case. You can delete your case and its documents at any time. We do not sell your data or use third-party advertising trackers.",
  },
  ];
}

export default function PricingPage() {
  const [fees, setFees] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/pricing/public")
      .then(({ data }) => {
        if (!alive) return;
        setFees({
          base: Math.round((data.fee_per_case_cents || 0) / 100),
          add: Math.round((data.fee_per_additional_beneficiary_cents || 0) / 100),
        });
      })
      .catch((e) => {
        // Non-critical: leave fallback numbers in place. Log so we can
        // debug if the public pricing endpoint ever regresses.
        console.error("[PricingPage] failed to load live pricing:", e);
      });
    load();
    // Refresh when the tab regains focus so an admin edit in another tab
    // shows up without a manual reload.
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const tiers = buildTiers(fees.base, fees.add);
  const faq = buildFaq(fees.base, fees.add);
  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-6 py-14" data-testid="pricing-page">
        <div className="text-center mb-12">
          <div className="pill pill-ai mb-3 inline-flex">Simple, transparent pricing</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold" style={{ color: "var(--text-primary)" }}>
            One transparent price per person. DS-260 always included.
          </h1>
          <p className="mt-4 text-[16px] max-w-2xl mx-auto" style={{ color: "var(--text-secondary)" }} data-testid="pricing-hero-copy">
            {money(fees.base)} per DS-260 applicant. {money(fees.add)} per additional family member on
            the same NVC case. Every person's DS-260 Consular Processing is
            included in the price — you never pay extra to consular-process a family
            member. No subscriptions. No hidden fees.
          </p>
          <div
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
            style={{
              background: "var(--ai-bg)",
              color: "var(--action)",
              border: "1px solid var(--ai-border)",
            }}
            data-testid="pricing-referral-hint"
          >
            🎁 Save $50 with a referral link from a friend — every family member
            you refer earns you both $50 off, too.
          </div>
        </div>

        {/* Tier cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={`card-surface p-6 flex flex-col ${
                t.highlight ? "ring-2" : ""
              }`}
              style={t.highlight ? { boxShadow: "0 0 0 2px var(--accent-copper)" } : {}}
              data-testid={`pricing-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t.highlight && (
                <div className="pill mb-3 inline-flex self-start"
                     style={{ background: "var(--accent-copper)", color: "#FFFFFF" }}>
                  Most popular
                </div>
              )}
              <h3 className="font-display text-xl font-bold" style={{ color: "var(--text-primary)" }}>
                {t.name}
              </h3>
              <div className="mt-3 mb-1">
                <span className="font-display text-4xl font-extrabold" style={{ color: "var(--text-primary)" }}>
                  {t.price}
                </span>
                <span className="text-sm ml-2" style={{ color: "var(--text-secondary)" }}>{t.period}</span>
              </div>
              <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>{t.description}</p>
              <ul className="space-y-2 mb-6 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-primary)" }}>
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#065F46" }} />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to={t.ctaLink}
                className={t.highlight ? "btn-primary text-sm flex items-center justify-center gap-1.5" : "btn-ghost text-sm flex items-center justify-center gap-1.5"}
                style={!t.highlight ? { border: "1px solid var(--border-stone)" } : {}}
                data-testid={`pricing-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {t.cta} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>

        {/* Credibility strip */}
        <div
          className="card-surface p-6 mb-16 flex items-start gap-4"
          data-testid="pricing-credibility-strip"
        >
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
               style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}>
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold mb-1" style={{ color: "var(--text-primary)" }}>
              Designed by a licensed U.S. immigration attorney
            </h3>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              DS-260USA.com was designed by a Licensed U.S. Immigration Attorney,
              admitted to practice since 1980, with over 35 years of experience
              handling U.S. immigration cases exclusively. Every workflow reflects
              real-world lessons from thousands of family-based petitions — helping
              you avoid the paperwork mistakes we've seen delay families for months
              or years.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <div className="pill pill-ai mb-2 inline-flex">
              <HelpCircle className="w-3.5 h-3.5 mr-1.5" /> Frequently asked questions
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold" style={{ color: "var(--text-primary)" }}>
              Answers to the questions families ask most
            </h2>
          </div>
          <div className="space-y-3 max-w-3xl mx-auto">
            {faq.map((item, i) => (
              <details
                key={item.q}
                className="card-surface p-5"
                data-testid={`pricing-faq-${i}`}
              >
                <summary className="font-semibold cursor-pointer text-sm sm:text-base" style={{ color: "var(--text-primary)" }}>
                  {item.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center py-10">
          <div className="inline-flex items-center gap-2 text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
            <Users className="w-4 h-4" style={{ color: "var(--accent-copper)" }} />
            Ready to bring your family together?
          </div>
          <Link to="/register" className="btn-primary text-base" data-testid="pricing-bottom-cta">
            Start your case — free to try
          </Link>
        </div>
      </div>
    </AppShell>
  );
}
